import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useApolloClient } from "@apollo/client";

import { Card, FilterTypes, PageHeader, Table } from "@cofano/cck";

import Helmet from "components/helmet/Helmet";
import { TableId, useBackendTable } from "components/table";

import CoreLogTypeSelect from "view/corelogs/components/selects/CoreLogTypeSelect";

import { FormattedDate } from "utils/DateUtils";

import { ChildPropsFromPagination } from "types/commons";
import { CoreLogType, SortDirection } from "__generated__/enums";
import { GetCoreLogsDocument, GetCoreLogsQuery } from "__generated__/view/corelogs/graphql/coreLog.queries.generated";

type CoreLogFromQuery = ChildPropsFromPagination<GetCoreLogsQuery, "coreLogs">

const CoreLogs = () => {
	const client = useApolloClient();
	const navigate = useNavigate();

	const { tableProps } = useBackendTable({
		id: TableId.CoreLogs,
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: GetCoreLogsDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "timestamp",
								direction: SortDirection.Desc,
							} ],
						},
						coreLogSearch: {
							...filter,
						},
					},
				});

				callback({
					content: result.data.coreLogs?.content ?? [],
					totalResults: result.data.coreLogs?.totalElements ?? 0,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{intl.formatMessage({ id: "CORE_LOGS" })}
					<Helmet title={intl.formatMessage({ id: "CORE_LOGS" })} />
				</PageHeader.Title>
			</PageHeader>

			<Card disablePadding>
				<Table
					{...tableProps}
					onRowDoubleClick={(row) => navigate(`/view/corelogs/${row.id}/details`)}
					columns={[ {
						id: "type",
						title: intl.formatMessage({ id: "TYPE" }),
						sortable: true,
						filterComponent: (value, onChange) => (
							<CoreLogTypeSelect
								label={intl.formatMessage({ id: "Type" })}
								value={value}
								onChange={(options) => onChange(options)}
							/>
						),
						render: (row: CoreLogFromQuery) => row.type === CoreLogType.In ? intl.formatMessage({ id: "IN" } ) : intl.formatMessage({ id: "OUT" } ),
					}, {
						id: "eventClass",
						title: intl.formatMessage({ id: "EVENT_CLASS" }),
						sortable: true,
						filterComponent: FilterTypes.TEXT,
					}, {
						id: "entityId",
						title: intl.formatMessage({ id: "ENTITY_ID" }),
						sortable: true,
						filterComponent: FilterTypes.TEXT,
					}, {
						id: "exceptionClass",
						title: intl.formatMessage({ id: "EXCEPTION_CLASS" }),
						sortable: true,
					}, {
						id: "targetCustomer",
						title: intl.formatMessage({ id: "TARGET_CUSTOMER" }),
						sortable: true,
					}, {
						id: "targetEnvironment",
						title: intl.formatMessage({ id: "TARGET_ENVIRONMENT" }),
						sortable: true,
					}, {
						id: "targetApplication",
						title: intl.formatMessage({ id: "TARGET_APPLICATION" }),
						sortable: true,
					}, {
						id: "errorMessage",
						title: intl.formatMessage({ id: "EXCEPTION_MESSAGE" }),
						sortable: true,
					}, {
						id: "timestamp",
						title: intl.formatMessage({ id: "TIMESTAMP" }),
						sortable: true,
						render: (row: CoreLogFromQuery) => <FormattedDate date={row.timestamp} withoutYear />,
					} ]} />
			</Card>
		</div>
	);
};

export default CoreLogs;
